import { useState, useRef } from 'react'
import { motion } from 'framer-motion'
import { HiMail, HiPhone, HiPaperAirplane } from 'react-icons/hi'
import { supabase } from '../lib/supabase'

export default function Contact({ content }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' })
  const [status, setStatus] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('sending')

    try {
      // Save to Supabase (admin panel)
      const { error: dbError } = await supabase
        .from('contact_messages')
        .insert([{
          name: form.name,
          phone: form.phone,
          email: form.email,
          message: form.message,
        }])
      
      if (dbError) throw dbError

      const formData = new FormData();
      formData.append("access_key", import.meta.env.VITE_WEB3FORMS_ACCESS_KEY);
      formData.append("name", form.name);
      formData.append("phone", form.phone);
      if (form.email) {
        formData.append("email", form.email);
      }
      formData.append("message", form.message);
      formData.append("subject", "הודעה חדשה מהאתר שלך!");

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        setStatus('success')
        setForm({ name: '', phone: '', email: '', message: '' })
      } else {
        throw new Error(result.message || 'Submission failed')
      }
    } catch (error) {
      console.error(error);
      setStatus('error')
    }
  }

  return (
    <section id="contact" className="py-24 px-6 bg-gradient-to-b from-white to-warm-light w-full flex flex-col items-center overflow-hidden">
      <div className="w-full max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <h2 className="font-rubik text-4xl md:text-5xl font-bold text-text mb-4">
            {content.contact_title}
          </h2>
          <p className="text-text-light text-lg max-w-xl mx-auto">
            {content.contact_text}
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          onSubmit={handleSubmit}
          className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-primary-light/10"
        >
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-text font-medium mb-2 text-sm">שם מלא</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={e => {
                  const val = e.target.value.replace(/[^a-zA-Zא-ת\s]/g, '');
                  setForm({ ...form, name: val })
                }}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary-light/30 outline-none transition-all text-text"
                placeholder="השם שלך"
              />
            </div>
            <div>
              <label className="block text-text font-medium mb-2 text-sm">טלפון</label>
              <input
                type="tel"
                required
                value={form.phone}
                onChange={e => {
                  const val = e.target.value.replace(/[^\d]/g, '');
                  setForm({ ...form, phone: val })
                }}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary-light/30 outline-none transition-all text-text"
                placeholder="050-0000000"
                dir="ltr"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-text font-medium mb-2 text-sm">אימייל (לא חובה)</label>
            <input
              type="email"
              value={form.email}
              onChange={e => {
                const val = e.target.value.replace(/[^a-zA-Z0-9.@_-]/g, '');
                setForm({ ...form, email: val })
              }}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary-light/30 outline-none transition-all text-text"
              placeholder="your@email.com"
              dir="ltr"
            />
          </div>

          <div className="mb-8">
            <label className="block text-text font-medium mb-2 text-sm">הודעה</label>
            <textarea
              required
              rows={4}
              value={form.message}
              onChange={e => {
                const val = e.target.value.replace(/[^a-zA-Zא-ת0-9\s.,!?'"\-()]/g, '');
                setForm({ ...form, message: val })
              }}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary-light/30 outline-none transition-all text-text resize-none"
              placeholder="ספרי לנו במה נוכל לעזור..."
            />
          </div>

          <button
            type="submit"
            disabled={status === 'sending'}
            className="w-full bg-primary hover:bg-primary-dark text-white py-4 rounded-full text-lg font-medium transition-all duration-300 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-4"
          >
            {status === 'sending' ? (
              'שולח...'
            ) : (
              <>
                <HiPaperAirplane className="rotate-180" />
                שליחה
              </>
            )}
          </button>

          <p className="text-[10px] text-text-muted text-center opacity-70 leading-tight">
            בלחיצה על שליחה, אני מסכימה לשמירת פרטיי במאגר המידע המאובטח לצורך מענה לפנייה זו, 
            בהתאם למדיניות הפרטיות המופיעה בתחתית האתר.
          </p>

          {status === 'success' && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center text-green-600 mt-4 font-medium"
            >
              ההודעה נשלחה בהצלחה! נחזור אלייך בהקדם
            </motion.p>
          )}

          {status === 'error' && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center text-red-500 mt-4"
            >
              שגיאה בשליחה. נסי שוב או צרי קשר בוואטסאפ
            </motion.p>
          )}
        </motion.form>
      </div>
    </section>
  )
}
